import { forwardRef, useImperativeHandle, useMemo, useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetLatestBillOfMaterialSummary } from "../../../../api/billOfMaterialSummary";
import { useGetSalesUnitByIngredient } from "../../../../api/ingredients";
import InputWithDropDown from "../../../../components/inputWithDropDown/InputWithDropDown";
import { ENABLE_IMPACT_RESULT_DOCUMENTS, LcalCulculationDefultValues } from "../../../../config/constants";
import { ImpactResultDocuments, ImpactResultDocumentsRef } from "./ImpactResultDocuments/ImpactResultDocuments";
import "../../ingredientRegister/IngredientRegister.scss";

interface DocumentItem {
  id: string;
  file: File;
  description: string;
  note: string;
  uploadedAt: Date;
}

interface SupplierIngredientLcaDataViewProps {
  dataView?: boolean;
  ingredientId: string;
  supplierId: string;
}

export interface SupplierIngredientLcaDataViewRef {
  supplierLCAData(): Record<string, { measure: number; unit: string }>;
  getDocuments(): DocumentItem[];
  getUploadedDocuments(): any[];
}

const SupplierIngredientLcaDataView = forwardRef<
  SupplierIngredientLcaDataViewRef,
  SupplierIngredientLcaDataViewProps
>(({ dataView = false, ingredientId, supplierId }, ref) => {
    const [uploadedDocuments, setUploadedDocuments] = useState<DocumentItem[]>([]);
    const documentsRef = useRef<ImpactResultDocumentsRef>(null);

    const { data: latestBillOfMaterialSummary, isFetching: isLatestBillofMaterialSummaryFetching } =
      useGetLatestBillOfMaterialSummary({
        ingredientId,
        supplierId
      });

    // Fetch existing supporting documents from API
    const {
      data: ingredientDetailData,
      isLoading: isLoadingIngredientDetail,
      refetch: refetchIngredientDetail
    } = useGetSalesUnitByIngredient({
      ingredientId,
      supplierId
    });

    // Load existing supporting documents from API response
    useEffect(() => {
      if (ingredientDetailData?.supportingDocuments && Array.isArray(ingredientDetailData.supportingDocuments)) {
        const existingDocs = ingredientDetailData.supportingDocuments.map((doc: any, index: number) => ({
          id: doc._id || `existing-${index}`,
          file: new File([], doc.fileName || `Document ${index + 1}`),
          description: doc.description || "",
          note: doc.note || "",
          uploadedAt: new Date(doc.uploadedAt || Date.now()),
          uploadedResponse: doc, // Store the API response
          isUploading: false
        }));
        setUploadedDocuments(existingDocs);
      } else if (ingredientDetailData && !ingredientDetailData.supportingDocuments) {
        setUploadedDocuments([]);
      }
    }, [ingredientDetailData, ingredientId, supplierId]);
    const values = useMemo(() => {
      return Object.keys(LcalCulculationDefultValues).reduce((acc, key) => {
        const defaultValue =
          LcalCulculationDefultValues[key as keyof typeof LcalCulculationDefultValues];
        acc[key] = {
          measure: latestBillOfMaterialSummary?.totalImpact?.[key]?.measure || null,
          unit: defaultValue.unit
        };
        return acc;
      }, {} as Record<string, { measure: number; unit: string }>);
    }, [latestBillOfMaterialSummary]);

    const { control, getValues } = useForm({
      mode: "all",
      values: values,
      defaultValues: values
    });

    useImperativeHandle(ref, () => ({
      supplierLCAData: () => {
        const rawValues = getValues();
        const formattedValues = Object.entries(LcalCulculationDefultValues).reduce(
          (acc, [key, defaultVal]) => {
            const input = rawValues[key];
            acc[key] = {
              measure: Number(input?.measure),
              unit: input?.unit || defaultVal.unit
            };
            return acc;
          },
          {} as Record<string, { measure: number; unit: string }>
        );
        return formattedValues;
      },
      getDocuments: () => {
        return documentsRef.current?.getDocuments() || [];
      },
      getUploadedDocuments: () => {
        return documentsRef.current?.getUploadedDocuments() || [];
      }
    }));

    if (isLatestBillofMaterialSummaryFetching) {
      return (
        <Dimmer active>
          <Loader content="Loading" />
        </Dimmer>
      );
    }

    return (
      <div>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <Grid className="customGridColomnTyp">
            {Object.keys(LcalCulculationDefultValues).map(key => {
              const { unit } =
                LcalCulculationDefultValues[key as keyof typeof LcalCulculationDefultValues];
              const label = key
                .split("_")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              return (
                <Grid.Column
                  key={key}
                  computer={4}
                  tablet={8}
                  mobile={16}
                  className="paddingRemoveTop"
                >
                  <InputWithDropDown
                    control={control}
                    labelName={label}
                    placeholder="0.00"
                    name={`${key}.measure`}
                    required={false}
                    customGridColumn="addCultivarCustomGridColumn"
                    disabled={dataView}
                    defaultSelectValue={unit}
                    options={[{ key: unit, text: unit, value: unit }]}
                    selectOption={`${key}.unit`}
                  />
                </Grid.Column>
              );
            })}
          </Grid>
        </Grid.Column>

        {/* Impact result documents section */}
        {ENABLE_IMPACT_RESULT_DOCUMENTS && (
          <Grid.Column computer={16} tablet={16} mobile={16} className="tw-mt-8">
            <ImpactResultDocuments
              ref={documentsRef}
              control={control}
              dataView={dataView}
              documents={uploadedDocuments}
              onDocumentsChange={setUploadedDocuments}
              ingredientId={ingredientId}
              supplierId={supplierId}
            />
          </Grid.Column>
        )}
      </div>
    );
  }
);

export default SupplierIngredientLcaDataView;
